import React, { useEffect, useRef } from 'react';
import {
  Button,
  ButtonProps,
  CircularProgress,
  CircularProgressProps
} from '@mui/material';

type LoadingButtonProps = ButtonProps & {
  loading?: boolean;
  disableWhileLoading?: boolean;
  loaderProps?: CircularProgressProps;
};

export const LoadingButton = ({
  loading,
  disableWhileLoading = true,
  loaderProps,
  children,
  ...btnProps
}: LoadingButtonProps) => {
  const btnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!loading && children) {
      const btn = btnRef.current;
      if (btn) {
        btn.style.minWidth = 'unset';
        btn.style.minHeight = 'unset';
        const t = setTimeout(() => {
          // offsetWidth/offsetHeight, not getBoundingClientRect(): inside a
          // Popover's Grow transition the rect is scaled mid-animation and
          // would freeze the button at a squeezed size. offsetWidth/Height
          // are unaffected by that transform and (unlike getComputedStyle's
          // resolved width/height, which is content-box in Firefox but
          // border-box in Chrome under box-sizing: border-box) are always
          // border-box layout integers, so they're cross-browser consistent.
          // Only min* is frozen (no max*): offsetWidth/Height round to the
          // nearest integer, so if the natural size is fractional a max*
          // pinned to the rounded-down value would clip below it and wrap
          // the label once loading ends.
          btn.style.minWidth = `${btn.offsetWidth}px`;
          btn.style.minHeight = `${btn.offsetHeight}px`;
        }, 5);
        return () => clearTimeout(t);
      }
    }
  }, [children, loading]);

  return (
    <Button
      {...btnProps}
      ref={btnRef}
      startIcon={loading ? undefined : btnProps.startIcon}
      endIcon={loading ? undefined : btnProps.endIcon}
      disabled={btnProps.disabled || (disableWhileLoading && loading)}
    >
      {loading && (
        <CircularProgress
          size={24}
          thickness={4}
          color="inherit"
          {...loaderProps}
        />
      )}
      {!loading && <>{children}</>}
    </Button>
  );
};
